'use client'

import Link from '@/components/Link'
import { colors } from '@/constants/theme'
import { TenantFromPrebuildFragment } from '@/generated/graphql'
import loadBaseMuiTheme from '@/helpers/loadBaseMuiTheme'
import useTenant from '@/hooks/useTenant'
import { main as legacyTheme } from '@caterdesk/ui--theme'
import {
  CssBaseline,
  ThemeProvider,
  createTheme,
  lighten,
  stepLabelClasses,
  alpha,
} from '@mui/material'

import React, { forwardRef } from 'react'
import { ThemeProvider as LegacyThemeProvider } from 'styled-components'

const LinkBehaviour = forwardRef(function LinkBehaviour(props, ref) {
  // @ts-expect-error forwardRef generic mismatch with wrapped Link props
  return <Link ref={ref} {...props} />
})

function removeNulls(obj: any): any {
  if (obj === null || obj === undefined) return undefined

  if (Array.isArray(obj)) {
    return obj.map(removeNulls)
  }

  if (typeof obj === 'object') {
    return Object.entries(obj)
      .filter(([_, value]) => value !== null)
      .reduce((newObj, [key, value]) => {
        newObj[key] = removeNulls(value)
        return newObj
      }, {} as any)
  }

  return obj
}

const baseMuiTheme = loadBaseMuiTheme()

const FeedrThemeProvider: React.FC<{
  children: React.ReactNode
  tenantOverride?: TenantFromPrebuildFragment
}> = ({ children, tenantOverride }) => {
  const tenant = useTenant()

  const selectedTenant = tenantOverride || tenant

  const tenantPalette = selectedTenant.marketing.theme?.palette
    ? removeNulls(selectedTenant.marketing.theme.palette)
    : baseMuiTheme.palette

  const mergedPalette = {
    orderStatus: baseMuiTheme.palette.orderStatus,
    ...tenantPalette,
  }

  // Service accents follow the tenant's own primary and secondary. A tenant
  // palette can omit either one, or their contrast text, so the base theme
  // backstops both. Without this an accent resolves with no `main` at all.
  const accentPrimary = { ...baseMuiTheme.palette.primary, ...mergedPalette.primary }
  const accentSecondary = { ...baseMuiTheme.palette.secondary, ...mergedPalette.secondary }

  const combinedPalette = {
    ...mergedPalette,
    canteen: accentPrimary,
    catering: accentSecondary,
    standingOrder: accentSecondary,
  }

  const selectedTheme = createTheme({
    ...baseMuiTheme,
    palette: combinedPalette,
    typography: {
      ...baseMuiTheme.typography,
      allVariants: { color: combinedPalette.text.primary },
      body3: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '0.00938em',
      },
    },
  })

  const primaryMain = selectedTheme.palette.primary.main ?? legacyTheme.colors.primary
  const secondaryMain = selectedTheme.palette.secondary.main ?? legacyTheme.colors.special
  const successMain = selectedTheme.palette.success.main ?? legacyTheme.colors.success
  const infoMain = selectedTheme.palette.info.main ?? legacyTheme.colors.info
  const errorMain = selectedTheme.palette.error.main ?? legacyTheme.colors.error

  const legacyColors = {
    ...legacyTheme.colors,
    primary: primaryMain,
    primary_main: primaryMain,
    primary_dark: selectedTheme.palette.primary.dark ?? legacyTheme.colors.primary_dark,
    primary_light: selectedTheme.palette.primary.light ?? legacyTheme.colors.primary_light,
    primary_lightest: lighten(primaryMain, 0.8),
    special: secondaryMain,
    special_main: secondaryMain,
    special_dark: selectedTheme.palette.secondary.dark ?? legacyTheme.colors.special_dark,
    special_light: selectedTheme.palette.secondary.light ?? legacyTheme.colors.special_light,
    special_lightest: lighten(secondaryMain, 0.8),
    success: successMain,
    success_main: successMain,
    success_dark: selectedTheme.palette.success.dark ?? legacyTheme.colors.success_dark,
    success_light: selectedTheme.palette.success.light ?? legacyTheme.colors.success_light,
    success_lightest: lighten(successMain, 0.8),
    info: infoMain,
    info_main: infoMain,
    info_dark: selectedTheme.palette.info.dark ?? legacyTheme.colors.info_dark,
    info_light: selectedTheme.palette.info.light ?? legacyTheme.colors.info_light,
    info_lightest: lighten(infoMain, 0.8),
    error: errorMain,
    error_main: errorMain,
    error_dark: selectedTheme.palette.error.dark ?? legacyTheme.colors.error_dark,
    error_light: selectedTheme.palette.error.light ?? legacyTheme.colors.error_light,
    error_lightest: lighten(errorMain, 0.8),
  }

  const legacyFontFaces = {
    ...legacyTheme.fontFaces,
    default: selectedTheme.typography.fontFamily,
  }

  const legacyTokens = {
    colors: legacyColors,
    fontSizes: legacyTheme.fontSizes,
    fontWeights: legacyTheme.fontWeights,
    fontFaces: legacyFontFaces,
    spacings: legacyTheme.spacings,
  }

  const customisedMuiTheme = {
    ...selectedTheme,
    ...legacyTokens,
    components: {
      ...selectedTheme.components,
      MuiLink: { defaultProps: { component: LinkBehaviour } },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontSize: 16,
            [`&.${stepLabelClasses.active}:not(.${stepLabelClasses.error})`]: {
              color: selectedTheme.palette.primary.main,
            },
          },
        },
      },
      MuiDialogContent: {
        defaultProps: {
          dividers: false,
        },
      },
    },
    palette: {
      ...selectedTheme.palette,
      ...colors,
      background: {
        ...selectedTheme.palette.background,
        canvas: baseMuiTheme.palette.background.canvas,
        subtle: baseMuiTheme.palette.background.subtle,
        hover: baseMuiTheme.palette.background.hover,
        border: baseMuiTheme.palette.background.border,
      },
      text: {
        ...selectedTheme.palette.text,
        muted: selectedTheme.palette.text?.muted ?? '#727373',
      },
    },
    borders: {
      light: `1px solid ${alpha(selectedTheme.palette.primary.main, 0.1)}`,
      medium: `1px solid ${alpha(selectedTheme.palette.primary.main, 0.4)}`,
      strong: `1px solid ${alpha(selectedTheme.palette.primary.main, 0.6)}`,
      default: `1px solid #EAEAEA`,
    },
    borderColors: {
      light: alpha(selectedTheme.palette.primary.main, 0.2),
      medium: alpha(selectedTheme.palette.primary.main, 0.4),
      strong: alpha(selectedTheme.palette.primary.main, 0.6),
      default: '#EAEAEA',
    },
  }

  const customisedLegacyTheme = {
    ...legacyTheme,
    ...legacyTokens,
  }

  return (
    <ThemeProvider theme={customisedMuiTheme}>
      {/* @ts-expect-error styled-components theme type differs from legacy token shape */}
      <LegacyThemeProvider theme={customisedLegacyTheme}>
        {children}
        <CssBaseline />
      </LegacyThemeProvider>
    </ThemeProvider>
  )
}

export default FeedrThemeProvider
